import React, { useEffect, useRef, useState } from "react";
import { Cnt } from "../../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK";
import { EMPTY_GUID } from "../../types/Guid";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPen } from "@fortawesome/free-solid-svg-icons";
import { Button, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import "./TreeNodeComponent.scss";
import { CreateEditModal } from "../CreateEditModal";
import { useUser } from "../../context/UserProvider";
import { AppSettings } from "../../AppSettings";

interface Props {
  node: Cnt.ThreeDeeCrate.SDK.ContainerDTO;
  onViewModels: (node: Cnt.ThreeDeeCrate.SDK.ContainerDTO, path: string[]) => void;
  pathToExpand: string[];
  currentPath: string[];
  selectedPath: string[];
  styleFilter: Cnt.ThreeDeeCrate.SDK.GraphicalStyleType;
  styleName: string; // "Standard" | "Stylized"...
}

export const TreeNodeComponent: React.FC<Props> = ({
  node,
  onViewModels,
  pathToExpand = [],
  currentPath,
  selectedPath,
  styleFilter,
  styleName,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<Cnt.ThreeDeeCrate.SDK.ContainerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(node.Name || "");
  const [description, setDescription] = useState(node.Description || "");
  const [type, setType] = useState(node.ContainerType || Cnt.ThreeDeeCrate.SDK.ContainerType.Container);
  const [visibility, setVisibility] = useState(node.Visibility || Cnt.ThreeDeeCrate.SDK.VisibilityType.Private);

  const navigate = useNavigate();
  const fullPath = [...currentPath, node.Name ?? ""];
  const isSelected = JSON.stringify(fullPath) === JSON.stringify(selectedPath);

  const { user } = useUser();
  const isAdmin = user?.IsAdmin === true;

  const PAGE_SIZE = 30;

  const buildCategoryPath = (segments: string[]) =>
    segments.map(s => encodeURIComponent(s)).join("--");

  const navigateWithStyle = (segments: string[], replace = false) => {
    const encodedCategory = buildCategoryPath(segments);
    navigate(`/${encodeURIComponent(styleName)}/${encodedCategory}`, { replace });
  };

  const loadChildren = async (reset: boolean = false) => {
    const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
    if (reset) setLoading(true);
    try {
      const containers = await api.container.GetContainers(node.Id ?? EMPTY_GUID, 0, PAGE_SIZE, styleFilter);
      setChildren(reset ? containers : (prev) => [...prev, ...containers]);
    } catch (err) {
      console.error("❌ Error loading children", err);
    } finally {
      if (reset) setLoading(false);
    }
  };

  const toggleExpand = async (shouldNavigate: boolean = true) => {
    const nextSegments = [...currentPath, node.Name ?? ""];
    if (node.ContainerType === Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer) {
      onViewModels?.(node, nextSegments);
      if (shouldNavigate) navigateWithStyle(nextSegments, false);
      return;
    }

    if (!expanded) await loadChildren(true);
    if (shouldNavigate) navigateWithStyle(nextSegments, false);

    onViewModels?.(node, nextSegments); // harmless for non-SearchContainer (parent ignores)
    setExpanded(prev => !prev);
  };

  const handleOpenCreate = () => {
    setEditMode(false);
    setName("");
    setDescription("");
    setType(Cnt.ThreeDeeCrate.SDK.ContainerType.Container);
    setShowModal(true);
  };

  const handleOpenEdit = () => {
    setEditMode(true);
    setName(node.Name || "");
    setDescription(node.Description || "");
    setType(node.ContainerType || Cnt.ThreeDeeCrate.SDK.ContainerType.Container);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);

      if (editMode) {
        const updatedNode = await api.container.Update({
          Id: node.Id,
          ParentId: node.ParentId,
          Name: name,
          Description: description,
          ContainerType: type,
          Visibility: visibility,
          GraphicalStyle: styleFilter,
        });
        node.Name = updatedNode.Name;
        node.ContainerType = updatedNode.ContainerType;
        node.Visibility = updatedNode.Visibility;
      } else {
        const newNode = await api.container.Create({
          Id: EMPTY_GUID,
          Name: name,
          Description: description,
          ParentId: node.Id ?? EMPTY_GUID,
          ContainerType: type,
          Visibility: visibility,
          GraphicalStyle: styleFilter,
        });
        setChildren((prev) => [...(prev || []), newNode]);
        if (!expanded) setExpanded(true);
      }

      setShowModal(false);
    } catch (err) {
      console.error("❌ Error saving node", err);
    }
  };

  // ---- SAFE AUTO-EXPAND (no navigation, no toggleExpand call) ----
  // Ensure we only attempt once per depth/segment to avoid loops.
  const lastAutoKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const expectedSegment = pathToExpand[currentPath.length];
    if (!expectedSegment) return;

    const key = `${currentPath.length}:${expectedSegment.toLowerCase()}`;
    if (lastAutoKeyRef.current === key) return;       // already tried for this segment
    if ((node.Name ?? "").toLowerCase() !== expectedSegment.toLowerCase()) return;

    lastAutoKeyRef.current = key;

    // For folders: just expand & load. For SearchContainer: notify parent.
    (async () => {
      if (node.ContainerType !== Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer) {
        if (!expanded) {
          await loadChildren(true);
          setExpanded(true);
        }
      } else {
        onViewModels?.(node, [...currentPath, node.Name ?? ""]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathToExpand, node.Name, currentPath.length]); // re-run when URL path changes

  // Reload children on style change if expanded/root
  useEffect(() => {
    if (expanded || node.Id === EMPTY_GUID) {
      loadChildren(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleFilter]);

  return (
    <div>
      {node.Id == EMPTY_GUID && isAdmin && (
        <OverlayTrigger placement="top" overlay={<Tooltip>Create Folder</Tooltip>}>
          <Button size="sm" variant="warning" className="icon-button w-100" onClick={handleOpenCreate}>
            <FontAwesomeIcon icon={faPlus} /> Create Folder
          </Button>
        </OverlayTrigger>
      )}

      {node.Id !== EMPTY_GUID && (
        <div
          className={`tree-node-row px-2 py-1 rounded d-flex align-items-center justify-content-between ${
            isSelected
              ? "bg-primary text-white"
              : visibility === Cnt.ThreeDeeCrate.SDK.VisibilityType.Private
              ? "text-danger"
              : "text-body"
          }`}
          draggable={isAdmin}
          onDragStart={(e) => e.dataTransfer.setData("text/plain", node.Id ?? "")}
          onDragOver={(e) => e.preventDefault()}
          onDrop={async (e) => {
            e.preventDefault();
            const draggedId = e.dataTransfer.getData("text/plain");
            if (!draggedId || draggedId === node.Id) return;
            try {
              const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
              await api.container.Move(draggedId, node.Id ?? EMPTY_GUID);
              await loadChildren(true);
            } catch (err) {
              console.error("❌ Failed to move node", err);
            }
          }}
          onClick={() => toggleExpand(true)}
        >
          <div style={{ flex: 1 }}>
            {node.ContainerType !== Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer ? (
              loading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <img
                  src={expanded ? "/folder-open.png" : "/folder.png"}
                  alt="Folder Toggle"
                  className="me-2"
                  style={{ width: 18, height: 18 }}
                />
              )
            ) : (
              <img src="/favicon.ico" className="me-2" style={{ width: 18, height: 18 }} />
            )}
            {node.Name}
          </div>

          {isAdmin && (
            <div>
              <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                <span role="button" className="icon-button ms-2" onClick={handleOpenEdit}>
                  <FontAwesomeIcon icon={faPen} />
                </span>
              </OverlayTrigger>
              {node.ContainerType !== Cnt.ThreeDeeCrate.SDK.ContainerType.SearchContainer && (
                <OverlayTrigger placement="top" overlay={<Tooltip>Create</Tooltip>}>
                  <span role="button" className="icon-button ms-2" onClick={handleOpenCreate}>
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
          )}
        </div>
      )}

      {(expanded || node.Id === EMPTY_GUID) && (
        <div style={{ marginLeft: node.Id !== EMPTY_GUID ? "12px" : 0 }}>
          {loading && (
            <div style={{ marginLeft: "12px" }}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {children.map((child) => (
            <TreeNodeComponent
              key={child.Id}
              node={child}
              onViewModels={onViewModels}
              pathToExpand={pathToExpand}
              currentPath={[...currentPath, node.Name ?? ""]}
              selectedPath={selectedPath}
              styleFilter={styleFilter}
              styleName={styleName}
            />
          ))}
        </div>
      )}

      <CreateEditModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        editMode={editMode}
        name={name}
        description={description}
        type={type}
        setName={setName}
        setDescription={setDescription}
        setType={setType}
        setVisibility={setVisibility}
        visibility={visibility}
      />
    </div>
  );
};
